'use client';
var __assign = (this && this.__assign) || function () {
    __assign = Object.assign || function(t) {
        for (var s, i = 1, n = arguments.length; i < n; i++) {
            s = arguments[i];
            for (var p in s) if (Object.prototype.hasOwnProperty.call(s, p))
                t[p] = s[p];
        }
        return t;
    };
    return __assign.apply(this, arguments);
};
var __spreadArray = (this && this.__spreadArray) || function (to, from, pack) {
    if (pack || arguments.length === 2) for (var i = 0, l = from.length, ar; i < l; i++) {
        if (ar || !(i in from)) {
            if (!ar) ar = Array.prototype.slice.call(from, 0, i);
            ar[i] = from[i];
        }
    }
    return to.concat(ar || Array.prototype.slice.call(from));
};
import { useId } from 'react';
import clsx from 'clsx';
import { Markdown } from './Markdown';
import { OpenAPICopyButton } from './OpenAPICopyButton';
import { OpenAPIDisclosure } from './OpenAPIDisclosure';
import { OpenAPISchemaName } from './OpenAPISchemaName';
import { retrocycle } from './decycle';
import { stringifyOpenAPI } from './stringifyOpenAPI';
import { tString } from './translate';
import { checkIsReference, resolveDescription, resolveFirstExample } from './utils';
/**
 * Render a property of an OpenAPI schema.
 */
function OpenAPISchemaProperty(props) {
    var parentCircularRefs = props.circularRefs, context = props.context, className = props.className, property = props.property;
    var schema = property.schema;
    var id = useId();
    return (<div id={id} className={clsx('openapi-schema', className)}>
            <OpenAPISchemaPresentation context={context} property={property}/>
            {(function () {
            var circularRefId = parentCircularRefs.get(schema);
            // Avoid recursing infinitely, and instead render a link to the parent schema
            if (circularRefId) {
                return <OpenAPISchemaCircularRef id={circularRefId} schema={schema}/>;
            }
            var circularRefs = new Map(parentCircularRefs);
            circularRefs.set(schema, id);
            var properties = getSchemaProperties(schema);
            if (properties === null || properties === void 0 ? void 0 : properties.length) {
                return (<OpenAPIDisclosure icon={context.icons.plus} label={function (isExpanded) {
                        return getDisclosureLabel({ schema: schema, isExpanded: isExpanded, context: context });
                    }}>
                            <OpenAPISchemaProperties properties={properties} circularRefs={circularRefs} context={context}/>
                        </OpenAPIDisclosure>);
            }
            var ancestors = new Set(circularRefs.keys());
            var alternatives = getSchemaAlternatives(schema, ancestors);
            if (alternatives) {
                return alternatives.map(function (schema, index) { return (<OpenAPISchemaAlternative key={index} schema={schema} circularRefs={circularRefs} context={context}/>); });
            }
            return null;
        })()}
        </div>);
}
/**
 * Render a set of properties of an OpenAPI schema.
 */
function OpenAPISchemaProperties(props) {
    var id = props.id, properties = props.properties, _a = props.circularRefs, circularRefs = _a === void 0 ? new Map() : _a, context = props.context;
    return (<div id={id} className="openapi-schema-properties">
            {properties.map(function (property, index) {
            return (<OpenAPISchemaProperty key={index} circularRefs={circularRefs} property={property} context={context}/>);
        })}
        </div>);
}
export function OpenAPISchemaPropertiesFromServer(props) {
    return (<OpenAPISchemaProperties id={props.id} properties={JSON.parse(props.properties, retrocycle())} context={props.context}/>);
}
/**
 * Render a root schema (such as the request body or response body).
 */
function OpenAPIRootSchema(props) {
    var schema = props.schema, context = props.context, _a = props.circularRefs, parentCircularRefs = _a === void 0 ? new Map() : _a;
    var id = useId();
    var properties = getSchemaProperties(schema);
    var description = resolveDescription(schema);
    if (properties === null || properties === void 0 ? void 0 : properties.length) {
        var circularRefs = new Map(parentCircularRefs);
        circularRefs.set(schema, id);
        return (<>
                {description ? (<Markdown source={description} className="openapi-schema-root-description"/>) : null}
                <OpenAPISchemaProperties properties={properties} circularRefs={circularRefs} context={context}/>
            </>);
    }
    return (<OpenAPISchemaProperty className="openapi-schema-root" property={{ schema: schema }} context={context} circularRefs={parentCircularRefs}/>);
}
export function OpenAPIRootSchemaFromServer(props) {
    return (<OpenAPIRootSchema schema={JSON.parse(props.schema, retrocycle())} context={props.context}/>);
}
/**
 * Render a tab for an alternative schema.
 * It renders directly the properties if relevant;
 * for primitives, it renders the schema itself.
 */
function OpenAPISchemaAlternative(props) {
    var schema = props.schema, circularRefs = props.circularRefs, context = props.context;
    var description = resolveDescription(schema);
    var properties = getSchemaProperties(schema);
    return (<>
            {description ? (<Markdown source={description} className="openapi-schema-description"/>) : null}
            <OpenAPIDisclosure icon={context.icons.plus} label={function (isExpanded) { return getDisclosureLabel({ schema: schema, isExpanded: isExpanded, context: context }); }}>
                {(properties === null || properties === void 0 ? void 0 : properties.length) ? (<OpenAPISchemaProperties properties={properties} circularRefs={circularRefs} context={context}/>) : (<OpenAPISchemaProperty property={{ schema: schema }} circularRefs={circularRefs} context={context}/>)}
            </OpenAPIDisclosure>
        </>);
}
/**
 * Render a circular reference to a schema.
 */
function OpenAPISchemaCircularRef(props) {
    var id = props.id, schema = props.schema;
    return (<div className="openapi-schema-circular">
            Circular reference to <a href={"#".concat(id)}>{getSchemaTitle(schema)}</a>{' '}
            <span className="openapi-schema-circular-glyph">↩</span>
        </div>);
}
/**
 * Render the enum value for a schema.
 */
function OpenAPISchemaEnum(props) {
    var schema = props.schema, context = props.context;
    var enumValues = (function () {
        var _a;
        // Render x-gitbook-enum first, as it has a different format
        if (schema['x-gitbook-enum']) {
            return Object.entries(schema['x-gitbook-enum']).map(function (_a) {
                var name = _a[0], description = _a[1].description;
                return {
                    value: name,
                    description: description,
                };
            });
        }
        if (schema['x-enumDescriptions']) {
            return Object.entries(schema['x-enumDescriptions']).map(function (_a) {
                var value = _a[0], description = _a[1];
                return {
                    value: value,
                    description: description,
                };
            });
        }
        return (_a = schema.enum) === null || _a === void 0 ? void 0 : _a.map(function (value) {
            return {
                value: value,
                description: undefined,
            };
        });
    })();
    if (!(enumValues === null || enumValues === void 0 ? void 0 : enumValues.length)) {
        return null;
    }
    return (<span className="openapi-schema-enum">
            Available options:{' '}
            {enumValues.map(function (item, index) { return (<span key={index} className="openapi-schema-enum-value">
                    <OpenAPICopyButton value={item.value} label={item.description} withTooltip={!!item.description} context={context}>
                        <code>{"".concat(item.value)}</code>
                    </OpenAPICopyButton>
                </span>); })}
        </span>);
}
/**
 * Render the top row of a schema. e.g: name, type, and required status.
 */
function OpenAPISchemaPresentation(props) {
    var _a = props.property, schema = _a.schema, propertyName = _a.propertyName, required = _a.required, context = props.context;
    var description = resolveDescription(schema);
    var example = resolveFirstExample(schema);
    return (<div className="openapi-schema-presentation">
            <OpenAPISchemaName schema={schema} type={getSchemaTitle(schema)} propertyName={propertyName} required={required} context={context}/>
            {typeof schema['x-deprecated-sunset'] === 'string' ? (<div className="openapi-deprecated-sunset openapi-schema-description openapi-markdown">
                    Sunset date:{' '}
                    <span className="openapi-deprecated-sunset-date">
                        {schema['x-deprecated-sunset']}
                    </span>
                </div>) : null}
            {description ? (<Markdown source={description} className="openapi-schema-description"/>) : null}
            {schema.default !== undefined ? (<span className="openapi-schema-default">
                    Default:{' '}
                    <code>
                        {typeof schema.default === 'string' && schema.default
                ? schema.default
                : stringifyOpenAPI(schema.default)}
                    </code>
                </span>) : null}
            {typeof example === 'string' ? (<span className="openapi-schema-example">
                    Example: <code>{example}</code>
                </span>) : null}
            {schema.pattern ? (<span className="openapi-schema-pattern">
                    Pattern: <code>{schema.pattern}</code>
                </span>) : null}
            <OpenAPISchemaEnum schema={schema} context={context}/>
        </div>);
}
/**
 * Get the sub-properties of a schema.
 */
function getSchemaProperties(schema) {
    // check array AND schema.items as this is sometimes null despite what the type indicates
    if (schema.type === 'array' && schema.items && !checkIsReference(schema.items)) {
        var items = schema.items;
        var itemProperties = getSchemaProperties(items);
        if (itemProperties) {
            return itemProperties;
        }
        // If the items are a primitive type, we don't need to display them
        if ((items.type === 'string' ||
            items.type === 'number' ||
            items.type === 'boolean' ||
            items.type === 'integer') &&
            !items.enum) {
            return null;
        }
        return [{ propertyName: 'items', schema: items }];
    }
    if (schema.type === 'object' || schema.properties) {
        var result_1 = [];
        if (schema.properties) {
            Object.entries(schema.properties).forEach(function (_a) {
                var propertyName = _a[0], propertySchema = _a[1];
                if (checkIsReference(propertySchema)) {
                    return;
                }
                result_1.push({
                    propertyName: propertyName,
                    required: Array.isArray(schema.required)
                        ? schema.required.includes(propertyName)
                        : undefined,
                    schema: propertySchema,
                });
            });
        }
        if (schema.additionalProperties && !checkIsReference(schema.additionalProperties)) {
            result_1.push({
                propertyName: 'Other properties',
                schema: schema.additionalProperties === true ? {} : schema.additionalProperties,
            });
        }
        return result_1;
    }
    return null;
}
/**
 * Get the alternatives to display for a schema.
 */
export function getSchemaAlternatives(schema, ancestors) {
    if (ancestors === void 0) { ancestors = new Set(); }
    var alternatives = (function () {
        if (schema.anyOf) {
            return ['anyOf', schema.anyOf];
        }
        if (schema.oneOf) {
            return ['oneOf', schema.oneOf];
        }
        if (schema.allOf) {
            return ['allOf', schema.allOf];
        }
        return null;
    })();
    if (!alternatives) {
        return null;
    }
    var type = alternatives[0], schemas = alternatives[1];
    return mergeAlternatives(type, flattenAlternatives(type, schemas, new Set(ancestors).add(schema)));
}
/**
 * Merge alternatives of the same type into a single schema.
 * - Merge string enums
 */
function mergeAlternatives(alternativeType, schemasOrRefs) {
    switch (alternativeType) {
        case 'oneOf': {
            return schemasOrRefs.reduce(function (acc, schemaOrRef) {
                var latest = acc.at(-1);
                if (latest &&
                    latest.type === 'string' &&
                    latest.enum &&
                    schemaOrRef.type === 'string' &&
                    schemaOrRef.enum) {
                    latest.enum = Array.from(new Set(__spreadArray(__spreadArray([], latest.enum, true), schemaOrRef.enum, true)));
                    latest.nullable = latest.nullable || schemaOrRef.nullable;
                    return acc;
                }
                acc.push(schemaOrRef);
                return acc;
            }, []);
        }
        case 'allOf': {
            return schemasOrRefs.reduce(function (acc, schemaOrRef) {
                var latest = acc.at(-1);
                if (latest &&
                    latest.type === 'string' &&
                    latest.enum &&
                    schemaOrRef.type === 'string' &&
                    schemaOrRef.enum) {
                    var keys = Object.keys(schemaOrRef);
                    if (keys.every(function (key) { return ['type', 'enum', 'nullable'].includes(key); })) {
                        latest.enum = Array.from(new Set(__spreadArray(__spreadArray([], latest.enum, true), schemaOrRef.enum, true)));
                        latest.nullable = latest.nullable || schemaOrRef.nullable;
                        return acc;
                    }
                }
                if (latest && latest.type === 'object' && schemaOrRef.type === 'object') {
                    var keys = Object.keys(schemaOrRef);
                    if (keys.every(function (key) {
                        return ['type', 'properties', 'required', 'nullable'].includes(key);
                    })) {
                        latest.properties = __assign(__assign({}, latest.properties), schemaOrRef.properties);
                        latest.required = Array.from(new Set(__spreadArray(__spreadArray([], (Array.isArray(latest.required) ? latest.required : []), true), (Array.isArray(schemaOrRef.required)
                            ? schemaOrRef.required
                            : []), true)));
                        latest.nullable = latest.nullable || schemaOrRef.nullable;
                        return acc;
                    }
                }
                acc.push(schemaOrRef);
                return acc;
            }, []);
        }
        default:
            return schemasOrRefs;
    }
}
function flattenAlternatives(alternativeType, schemasOrRefs, ancestors) {
    return schemasOrRefs.reduce(function (acc, schemaOrRef) {
        if (checkIsReference(schemaOrRef)) {
            return acc;
        }
        if (schemaOrRef[alternativeType] && !ancestors.has(schemaOrRef)) {
            var schemas = getSchemaAlternatives(schemaOrRef, ancestors);
            if (schemas) {
                acc.push.apply(acc, schemas);
            }
            return acc;
        }
        acc.push(schemaOrRef);
        return acc;
    }, []);
}
function getSchemaTitle(schema) {
    var _a;
    // Otherwise try to infer a nice title
    var type = 'any';
    if (schema.enum || schema['x-enumDescriptions'] || schema['x-gitbook-enum']) {
        type = "".concat(schema.type, " \u00B7 enum");
        // check array AND schema.items as this is sometimes null despite what the type indicates
    }
    else if (schema.type === 'array' && !!schema.items) {
        type = "".concat(getSchemaTitle(schema.items), "[]");
    }
    else if (Array.isArray(schema.type)) {
        type = schema.type.join(' | ');
    }
    else if (schema.type || schema.properties) {
        type = (_a = schema.type) !== null && _a !== void 0 ? _a : 'object';
        if (schema.format) {
            type += " \u00B7 ".concat(schema.format);
        }
    }
    if ('anyOf' in schema) {
        type = 'any of';
    }
    else if ('oneOf' in schema) {
        type = 'one of';
    }
    else if ('allOf' in schema) {
        type = 'all of';
    }
    else if ('not' in schema) {
        type = 'not';
    }
    return type;
}
function getDisclosureLabel(props) {
    var _a, _b;
    var schema = props.schema, isExpanded = props.isExpanded, context = props.context;
    var label;
    if (schema.type === 'array' && !!schema.items) {
        if (schema.items.oneOf) {
            label = tString(context.translation, 'available_items').toLowerCase();
        }
        // Fallback to "child attributes" for enums and objects
        else if (schema.items.enum || schema.items.type === 'object') {
            label = tString(context.translation, 'child_attributes').toLowerCase();
        }
        else {
            label = (_b = (_a = schema.items.title) !== null && _a !== void 0 ? _a : schema.title) !== null && _b !== void 0 ? _b : getSchemaTitle(schema.items);
        }
    }
    else {
        label = schema.title || tString(context.translation, 'child_attributes').toLowerCase();
    }
    return "".concat(isExpanded ? tString(context.translation, 'hide') : tString(context.translation, 'show'), " ").concat(label);
}
